<template>
    <div>
        <div class="flex justify-center card-header py-2">
            <h3 id="popups.title.addpopup">{{ title }}</h3>
        </div>

        <div v-if="($route.params.popupId && !isRetrievingPopup) || !$route.params.popupId" class="card-body mt-4">
            <form class="space-y-8" @submit.prevent="handleSave">
                <div class="grid grid-cols-2 gap-y-8 gap-x-4">
                    <!-- Pop Up Name -->
                    <div>
                        <label for="popup-name" class="block text-sm font-bold text-gray-700">
                            {{ translate('Popup Name') }}
                            <span class="text-red-500">*</span>
                        </label>
                        <input v-model="form.label" type="text" id="popups.input.popupname"
                            name="popups.input.popupname"
                            class="mt-1 h-11 block w-full border border-red-400 rounded px-3 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                            :placeholder="translate('Popup Name')" >
                        <div v-if="formErrors['label'] && !form.label" :class="`text-xs mt-1 text-red-500 ${getCurrentLanguage === 'ar-sa' ? 'flex justify-start' : ''}`">
                            {{ formErrors['label'] }}
                        </div>
                    </div>

                    <!-- Idle Seconds -->
                    <div>
                        <label for="idle-seconds" class="block text-sm font-bold text-gray-700">
                            {{ translate('Idle Time(s)') }}
                            <span class="text-red-500">*</span>
                        </label>
                        <input v-model="form['idle-seconds']" type="number" id="popups.input.idleseconds"
                            :max="120"
                            :mix="0"
                            name="popups.input.idleseconds"
                            class="mt-1 h-11 block w-full border border-red-400 rounded px-3 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                            :placeholder="translate('Idle Time')">
                        <div v-if="formErrors['idle-seconds'] && !form['idle-seconds']" :class="`text-xs mt-1 text-red-500 ${getCurrentLanguage === 'ar-sa' ? 'flex justify-start' : ''}`">
                            {{ formErrors['idle-seconds'] }}
                        </div>
                    </div>

                    <!-- Button Name Arabic -->
                    <div>
                        <label for="button-name-ar" class="block text-sm font-bold text-gray-700">
                            {{ translate('Button Name - Arabic') }}
                            <span class="text-red-500">*</span>
                        </label>
                        <input v-model="form['button-label']['ar-sa']" type="text" id="popups.input.buttonnamear"
                            name="popups.input.buttonnamear"
                            class="mt-1 h-11 block w-full border border-red-400 rounded px-3 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                            :placeholder="translate('Button Name Arabic')" >
                        <div v-if="formErrors['button-label'] && !form['button-label']['ar-sa']" :class="`text-xs mt-1 text-red-500 ${getCurrentLanguage === 'ar-sa' ? 'flex justify-start' : ''}`">
                            {{ formErrors['button-label']['ar-sa'] }}
                        </div>
                    </div>

                    <!-- Button Name English -->
                    <div>
                        <label for="button-name-en" class="block text-sm font-bold text-gray-700">
                            {{ translate('Button Name - English') }}
                            <span class="text-red-500">*</span>
                        </label>
                        <input v-model="form['button-label']['en-us']" type="text" id="popups.input.buttonnameen"
                            name="popups.input.buttonnameen"
                            class="mt-1 h-11 block w-full border border-red-400 rounded px-3 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                            :placeholder="translate('Button Name English')">
                        <div v-if="formErrors['button-label'] && !form['button-label']['en-us']" :class="`text-xs mt-1 text-red-500 ${getCurrentLanguage === 'ar-sa' ? 'flex justify-start' : ''}`">
                            {{ formErrors['button-label']['en-us'] }}
                        </div>
                    </div>

                    <!-- Image Fields -->
                    <div :class="`flex ${getCurrentLanguage === 'ar-sa' ? '' : 'space-x-4'}`">
                        <!-- Image Field Arabic -->
                        <div :class="`w-1/3 ${getCurrentLanguage === 'ar-sa' ? 'ml-3' : ''}`">
                            <label for="image-type-ar" class="block text-sm font-bold text-gray-700">
                                {{ translate('Image - Arabic') }}
                            </label>
                            <select v-model="imageTypeAr" id="popups.label.imagetypear" name="popups.label.imagetypear"
                                class="mt-1 h-11 block w-full border border-red-400 rounded px-3 focus:ring-blue-500 focus:border-blue-500 sm:text-sm">
                                <option v-for="type in imageTypes" :key="type.value" :value="type.value">
                                    {{ type.text }}
                                </option>
                            </select>
                        </div>
                        <div class="w-2/3">
                            <label for="image-uri-ar" class="block text-sm font-bold text-gray-700">
                                {{ translate('Image URL - Arabic') }}
                                <span class="text-red-500">*</span>
                            </label>

                            <div class="w-full">
                                <!-- File Input Container -->
                                <div v-if="imageTypeAr === 'upload-img'"
                                    class="flex items-center border border-gray-300 rounded overflow-hidden h-11">
                                    <input ref="fileInputAr" id="popups.input.imageuriar" name="popups.input.imageuriar"
                                        type="file" class="hidden" @change="onFileChange('ar-sa', $event)"
                                        accept="image/*" />

                                    <!-- File Name -->
                                    <div class="flex-grow px-3 py-2 text-gray-700 text-sm truncate bg-white cursor-pointer"
                                        @click="$refs.fileInputAr.click()">
                                        {{ fileImageAr ? (fileImageAr.name || fileImageAr) : 'No file chosen' }}
                                    </div>

                                    <!-- Browse Button -->
                                    <button id="popups.button.browsefilear" name="popups.button.browsefilear"
                                        type="button"
                                        class="bg-gray-100 hover:bg-gray-200 h-11 text-sm px-4 py-2 border-0"
                                        @click="$refs.fileInputAr.click()">
                                        {{ translate('Browse') }}
                                    </button>
                                </div>
                                <div v-else>
                                    <input v-model="form['image-uri']['ar-sa']" type="text" id="popups.input.imageuriar"
                                        name="popups.input.imageuriar"
                                        class="mt-1 h-11 block w-full border border-red-400 rounded px-3 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                        :placeholder="translate('Image URL')">
                                </div>
                                <div v-if="formErrors['image-uri'] && !form['image-uri']['ar-sa']" :class="`text-xs mt-1 text-red-500 ${getCurrentLanguage === 'ar-sa' ? 'flex justify-start' : ''}`">
                                    {{ formErrors['image-uri']['ar-sa'] }}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div :class="`flex ${getCurrentLanguage === 'ar-sa' ? '' : 'space-x-4'}`">
                        <!-- Image Field English -->
                        <div :class="`w-1/3 ${getCurrentLanguage === 'ar-sa' ? 'ml-3' : ''}`">
                            <label for="popups.label.imagetypeen" class="block text-sm font-bold text-gray-700">
                                {{ translate('Image - English') }}
                            </label>
                            <select v-model="imageTypeEn" id="popups.select.imagetypeen"
                                name="popups.select.imagetypeen"
                                class="mt-1 h-11 block w-full border border-red-400 rounded px-3 focus:ring-blue-500 focus:border-blue-500 sm:text-sm">
                                <option v-for="type in imageTypes" :key="type.value" :value="type.value">
                                    {{ type.text }}
                                </option>
                            </select>
                        </div>
                        <div class="w-2/3">
                            <label for="popups.label.imageurien" class="block text-sm font-bold text-gray-700">
                                {{ translate('Image URL - English') }}
                                <span class="text-red-500">*</span>
                            </label>
                            <div class="w-full">
                                <!-- File Input Container -->
                                <div v-if="imageTypeEn === 'upload-img'"
                                    class="flex items-center border border-gray-300 rounded overflow-hidden h-11">
                                    <input ref="fileInputEn" id="popups.input.imageurien" name="popups.input.imageurien"
                                        type="file" class="hidden" @change="onFileChange('en-us', $event)"
                                        accept="image/*" />

                                    <!-- File Name -->
                                    <div class="flex-grow px-3 py-2 text-gray-700 text-sm truncate bg-white cursor-pointer"
                                        @click="$refs.fileInputEn.click()">
                                        {{ fileImageEn ? (fileImageEn.name || fileImageEn) : 'No file chosen' }}
                                    </div>

                                    <!-- Browse Button -->
                                    <button id="popups.button.browsefileen" name="popups.button.browsefileen"
                                        type="button"
                                        class="bg-gray-100 hover:bg-gray-200 h-11 text-sm px-4 py-2 border-0"
                                        @click="$refs.fileInputEn.click()">
                                        {{ translate('Browse') }}
                                    </button>
                                </div>

                                <div v-else>
                                    <input v-model="form['image-uri']['en-us']" type="text" id="popups.input.imageurien"
                                        name="popups.input.imageurien"
                                        class="mt-1 h-11 block w-full border border-red-400 rounded px-3 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                        :placeholder="translate('Image URL')">
                                </div>
                                <div v-if="formErrors['image-uri'] && !form['image-uri']['en-us']" :class="`text-xs mt-1 text-red-500 ${getCurrentLanguage === 'ar-sa' ? 'flex justify-start' : ''}`">
                                    {{ formErrors['image-uri']['en-us'] }}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div
                    :class="[`grid ${['product', 'category'].includes(selectedActionType) ? 'grid-cols-3' : 'grid-cols-2'} gap-x-4`]">
                    <!-- Action Type -->
                    <div class="w-full">
                        <label for="action-type" class="block text-sm font-bold text-gray-700">
                            {{ translate('Type') }}
                            <span class="text-red-500">*</span>
                        </label>

                        <select v-model="form['action-type']" id="popups.select.actiontype"
                            name="popups.select.actiontype"
                            class="mt-1 h-11 block w-full border border-red-400 rounded px-3 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                            @change="updateButtonTypeHandler(form['action-type'])">
                            <option v-for="type in buttonTypes" :key="type.value" :value="type.value">
                                {{ type.text }}
                            </option>
                        </select>
                        <div v-if="formErrors['action-type'] && !form['action-type']" :class="`text-xs mt-1 text-red-500 ${getCurrentLanguage === 'ar-sa' ? 'flex justify-start' : ''}`">
                            {{ formErrors['action-type'] }}
                        </div>
                    </div>
                    <!-- Menu ID -->
                    <div v-if="['product', 'category'].includes(selectedActionType)" class="w-full">
                        <label for="action-menu-id" class="block text-sm font-bold text-gray-700">
                            {{ translate('Menu') }}
                            <span class="text-red-500">*</span>
                        </label>

                        <select v-model="form['action-menu-id']" id="popups.select.actionmenuid"
                            name="popups.select.actionmenuid"
                            class="mt-1 h-11 block w-full border border-red-400 rounded px-3 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                            @change="onChangeMenu">
                            <option v-for="menu in buttonMenus" :key="menu.id" :value="menu.id">
                                {{ menu.attributes.label }}
                            </option>
                        </select>
                        <div v-if="formErrors['action-menu-id'] && !form['action-menu-id']" :class="`text-xs mt-1 text-red-500 ${getCurrentLanguage === 'ar-sa' ? 'flex justify-start' : ''}`">
                            {{ formErrors['action-menu-id'] }}
                        </div>
                    </div>
                    <!-- Item ID -->
                    <div class="w-full">
                        <label for="action-value" class="block text-sm font-bold text-gray-700">
                            {{ selectedActionType === 'category' ? translate('Categories') : selectedActionType ===
                                'product' ? translate('Items') : translate('URL') }}
                            {{ isRetrievingItemsCategories ? ' - Retrieving' : '' }}
                            <i v-if="isRetrievingItemsCategories" class="fas fa-spinner fa-spin"></i>
                            <span class="text-red-500">*</span>
                        </label>
                        <!-- Items Dropdown -->
                        <select v-if="['product', 'category'].includes(selectedActionType)"
                            v-model="form['action-value']" id="popups.select.actionvalue"
                            name="popups.select.actionvalue" :disabled="buttonItemsCategories.length === 0"
                            class="mt-1 h-11 block w-full border border-red-400 rounded px-3 focus:ring-blue-500 focus:border-blue-500 sm:text-sm">
                            <option v-for="itemCategory in buttonItemsCategories" :key="itemCategory.id"
                                :value="itemCategory.id">
                                {{ itemCategory.attributes.name }}
                            </option>
                        </select>
                        <!-- URL Input -->
                        <input v-else v-model="form['action-value']" type="text" id="popups.input.actionvalue"
                            name="popups.input.actionvalue"
                            class="mt-1 h-11 block w-full border border-red-400 rounded px-3 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                            :placeholder="translate('URL')">
                        <div v-if="formErrors['action-value'] && !form['action-value']" :class="`text-xs mt-1 text-red-500 ${getCurrentLanguage === 'ar-sa' ? 'flex justify-start' : ''}`">
                            {{ formErrors['action-value'] }}
                        </div>
                    </div>
                </div>

                <div class="flex justify-end space-x-1 mt-5">
                    <BaseButton id="popups.button.cancelpopupform" :disabled="isSaving"
                        customClass="btn btn-secondary w-28" @click="handleCancel">
                        {{ translate('Cancel') }}
                    </BaseButton>
                    <BaseButton id="popups.button.savepopupform" :disabled="isSaving" type="submit"
                        customClass="btn btn-twitter w-28">
                        <span>
                            {{ isSaving ? translate('Saving...') : translate('Save') }}
                        </span>
                    </BaseButton>
                </div>
            </form>
        </div>

        <div v-if="$route.params.popupId && isRetrievingPopup" class="p-4 text-center h-96 grid place-items-center">
            <div class="text-center">
                <i class="fas fa-spinner fa-spin text-3xl"></i>
            </div>
        </div>
    </div>
</template>

<script>
import { eventHandler, translations } from '@/mixins'
import BaseButton from '@/components/Buttons/BaseButton.vue';
import { mapGetters } from "vuex"
import { Menu as MenuApi } from "@/services/SOLO"
import { Banner as BannerApi } from "@/services/SOLO"
import { Popup as PopupApi } from "@/services/SOLO"

export default {
    name: 'CreatePopup',
    mixins: [eventHandler, translations],
    components: {
        BaseButton
    },
    data() {
        return {
            form: {
                label: '',
                concept_id: '',
                'idle-seconds': 0,
                occurrences: 0,
                'button-label': {
                    'en-us': '',
                    'ar-sa': ''
                },
                'image-uri': {
                    'en-us': '',
                    'ar-sa': ''
                },
                'action-type': '',
                'action-value': '',
                'action-menu-id': '',
            },
            imageTypeAr: 'upload-img',
            imageTypeEn: 'upload-img',
            fileImageEn: null,
            fileImageAr: null,
            imageTypes: [
                { value: 'upload-img', text: "Upload IMG", },
                { value: 'image-url', text: "Image URL", },
            ],
            buttonTypes: [
                { value: "product", text: "Product" },
                { value: "category", text: "Category" },
                { value: "url", text: "URL" },
            ],
            buttonMenus: [],
            buttonItemsCategories: [],
            selectedActionType: null,
            isSaving: false,
            isRetrievingItemsCategories: false,
            isRetrievingPopup: false,
            formErrors: {}
        }
    },
    computed: {
        ...mapGetters({
            getLocale: 'app/getLocale',
            getConceptSettings: 'account/getConceptSettings',
        }),
        title() {
            return this.$route.params.popupId ? this.translate('Edit Popup') : this.translate('Add Popup');
        },
        getCurrentLanguage() {
            const localeMap = {
                ar_SA: 'ar-sa',
                en_US: 'en-us',
                fr_FR: 'fr-fr'
            };
            return localeMap[this.getLocale] || 'en-us';
        }
    },
    watch: {
        fileImageEn(newVal) {
            if (newVal) {
                this.form['image-uri']['en-us'] = newVal;
            }
        },
        fileImageAr(newVal) {
            if (newVal) {
                this.form['image-uri']['ar-sa'] = newVal;
            }
        },
        form: {
            deep: true,
            handler(newVal) {
                this.selectedActionType = newVal['action-type'] || null;
            }
        },
    },
    created() {
        /** For Editing Pop Up */
        if (this.$route.params.popupId) {
            this.getPopup(this.$route.params.popupId);
        }
    },
    mounted() {
        this.getMenus();
    },
    methods: {
        async getPopup(popupId) {
            try {
                this.isRetrievingPopup = true;
                const response = await PopupApi.find(popupId);
                const popup = response.data.data;
                this.form = {
                    ...this.form,
                    ...popup.attributes,
                    'button-label': popup.attributes['button-label-raw'] || { 'en-us': '', 'ar-sa': '' },
                    'image-uri': popup.attributes['image-uri-raw'] || { 'en-us': '', 'ar-sa': '' },
                };
                const attrsToRemoved = ['image-uri-raw', 'button-label-raw', 'created-at', 'updated-at']
                this.form = Object.fromEntries(
                    Object.entries(this.form).filter(([key]) => !attrsToRemoved.includes(key))
                );
                this.fileImageAr = this.form['image-uri']['ar-sa'];
                this.fileImageEn = this.form['image-uri']['en-us'];
                this.updateButtonTypeHandler(this.form['action-type']);
            } catch (error) {
                this.$notify({
                    title: "ERROR FETCHING POPUP!",
                    verticalAlign: "bottom",
                    horizontalAlign: "left",
                    message: "Something went wrong, please try again!",
                    type: "danger",
                    icon: "fas fa-bomb",
                });
            } finally {
                this.isRetrievingPopup = false;
            }
        },
        handleCancel() {
            this.$router.push({ name: 'popups' });
        },
        onFileChange(lang, event) {
            const file = event.target.files[0];
            if (!file) return;

            const isImage = file.type.startsWith('image/');
            const maxSize = 2 * 1024 * 1024; // 2 MB

            if (!isImage) {
                this.$notify({
                    title: 'SYSTEM WARNING!',
                    verticalAlign: 'bottom',
                    horizontalAlign: 'left',
                    message: 'File must be an image.',
                    type: "warning",
                    icon: "fas fa-info-circle"
                });
                event.target.value = '';
                return;
            }

            if (file.size > maxSize) {
                this.$notify({
                    title: 'SYSTEM WARNING!',
                    verticalAlign: 'bottom',
                    horizontalAlign: 'left',
                    message: 'File size must be less than 2 MB.',
                    type: "warning",
                    icon: "fas fa-info-circle"
                });
                event.target.value = '';
                return;
            }
            this[`fileImage${lang === 'ar-sa' ? 'Ar' : 'En'}`] = file;
        },
        async getMenus() {
            try {
                const response = await MenuApi.all();
                this.buttonMenus = response.data.data;
            } catch (error) {
                this.$notify({
                    title: "ERROR FETCHING MENUS!",
                    verticalAlign: "bottom",
                    horizontalAlign: "left",
                    message: "Something went wrong, please try again!",
                    type: "danger",
                    icon: "fas fa-bomb",
                });
            }
        },
        onChangeMenu() {
            this.updateButtonTypeHandler(this.selectedActionType);
        },
        async updateButtonTypeHandler(type, page = 1) {
            try {
                let response;
                this.buttonItemsCategories = [];
                if (!this.form['action-menu-id']) return;

                this.isRetrievingItemsCategories = true;

                if (type === 'product') {
                    response = await BannerApi.linkValueSearch(this.form['action-menu-id'], 'items', page);
                }

                if (type === 'category') {
                    response = await BannerApi.linkValueSearch(this.form['action-menu-id'], 'categories', page);
                }

                if (response?.data?.data && Array.isArray(response.data.data)) {
                    this.buttonItemsCategories = [...this.buttonItemsCategories, ...response.data.data];
                }

                const pagination = response?.data?.meta?.pagination;
                if (pagination?.current_page < pagination?.total_pages) {
                    this.updateButtonTypeHandler(type, pagination.current_page + 1);
                }

                this.$forceUpdate();
            } catch (error) {
                this.$notify({
                    title: "ERROR FETCHING ITEMS CATEGORIES!",
                    verticalAlign: "bottom",
                    horizontalAlign: "left",
                    message: "Something went wrong, please try again!",
                    type: "danger",
                    icon: "fas fa-bomb",
                });
            } finally {
                this.isRetrievingItemsCategories = false;
            }
        },
        validateForm(requiredFields, form) {
            this.formErrors = {};

            requiredFields.forEach(field => {
                const value = form[field];

                if (value === null || value === undefined || value === '') {
                    this.formErrors[field] = this.translate('This field is required');
                    return;
                }

                const missingLangs = Object.entries(value).filter(([_, v]) => !v).map(([lang]) => lang);
                if (missingLangs.length > 0) {
                    this.formErrors[field] = {};

                    missingLangs.forEach(lang => {
                        this.formErrors[field][lang] = this.translate('This field is required');
                    });
                }
            });
            
            /** True = no error else has en errors*/
            return Object.keys(this.formErrors).length === 0;
        },
        async handleSave() {
            let requiredFields = ['label', 'idle-seconds', 'image-uri', 'button-label', 'action-menu-id', 'action-value', 'action-type']
            let fieldsToCheck = [...requiredFields];
            if (this.form['action-type'] === 'url') {
                fieldsToCheck = fieldsToCheck.filter(field => field !== 'action-menu-id');
            }
            if (!this.validateForm(fieldsToCheck, this.form)) return

            try {
                this.isSaving = true;
                const formData = new FormData();
                // Basic flat fields
                const keys = ['label', 'idle-seconds', 'occurrences', 'action-type', 'action-value', 'action-menu-id'];
                for (const key of keys) {
                    if (this.form[key] !== undefined && this.form[key] !== null) {
                        formData.append(`popup[${key}]`, this.form[key]);
                    }
                }

                // Localized button-label and image-uri
                for (const lang of Object.keys(this.form['button-label'])) {
                    const label = this.form['button-label'][lang];
                    if (label) {
                        formData.append(`popup[button-label][${lang}]`, label);
                    }

                    const image = this.form['image-uri'][lang];
                    if (image) {
                        // Support both URL (string) and File objects
                        formData.append(`popup[image-uri][${lang}]`, image);
                    }
                }
                if (this.$route.params.popupId) {
                    formData.append(
                        `_method`,
                        'PATCH'
                    )
                    await PopupApi.update(formData, this.$route.params.popupId)
                } else {
                    await PopupApi.create(formData);
                }
                this.$notify({
                    title: `POPUP ${this.$route.params.popupId ? 'UPDATED' : 'CREATED'}!`,
                    verticalAlign: "bottom",
                    horizontalAlign: "left",
                    message: `Popup has been ${this.$route.params.popupId ? 'updated' : 'created'} successfully`,
                    type: "success",
                    icon: "fas fa-check",
                });
                this.$router.push({ name: 'popups' });
            } catch (err) {
                this.$notify({
                    title: "ERROR SAVING POPUP!",
                    verticalAlign: "bottom",
                    horizontalAlign: "left",
                    message: "Something went wrong, please try again!",
                    type: "danger",
                    icon: "fas fa-bomb",
                });
            } finally {
                this.isSaving = false;
            }
        }
    },
}
</script>
